import type { Discover, SsoConfig } from "@digiits-agency/polaris-sdk";
import { polaris } from "./polaris-client";

/** One SSO button on an auth screen — an entry of `SsoConfig.providers`. */
export type SsoConfigProvider = SsoConfig["providers"][number];

export type SsoEnforcement = SsoConfig["ssoEnforcement"];
export type SsoMode = "login" | "signup";

export interface SsoConfigResult {
  providers: SsoConfigProvider[];
  /**
   * Tenant-wide default. Per-address enforcement is decided by the verified
   * domain the address belongs to and only `discoverLoginMethod` knows it —
   * this is the fallback when there is no email to discover against yet.
   */
  ssoEnforcement: SsoEnforcement;
}

const EMPTY_CONFIG: SsoConfigResult = {
  providers: [],
  ssoEnforcement: "disabled",
};

/**
 * The enabled SSO connections for the login/signup screens.
 *
 * Public and best-effort: a workspace with no SSO configured answers with an
 * empty list, and a gateway that is down must not block password login, so a
 * failure degrades to "no SSO" rather than an error banner.
 */
export async function loadSsoConfig(
  tenantId?: string,
): Promise<SsoConfigResult> {
  try {
    const res = await polaris.auth.getConfig(
      tenantId ? { query: { tenant_id: tenantId } } : {},
    );
    return {
      providers: res?.providers ?? [],
      ssoEnforcement: res?.ssoEnforcement ?? "disabled",
    };
  } catch {
    return EMPTY_CONFIG;
  }
}

/** Back-compat shim for callers that only want the buttons. */
export async function loadSsoProviders(
  tenantId?: string,
): Promise<SsoConfigProvider[]> {
  return (await loadSsoConfig(tenantId)).providers;
}

/**
 * What the login screen should render for one email address.
 *
 * Resolved from the address's *domain* against the tenant's verified domains,
 * so it — not `loadSsoConfig().ssoEnforcement` — is what decides whether a
 * password box is offered to this particular person.
 */
export async function discoverLoginMethod(email: string): Promise<Discover> {
  return polaris.auth.discoverLoginMethod({ body: { email } });
}

/** Where the IdP sends the browser back to. Handled by `AuthCallbackPage`. */
function callbackUrl(): string {
  return `${window.location.origin}/auth/callback`;
}

export async function startSso(
  provider: Pick<SsoConfigProvider, "connectionId">,
  opts: {
    mode: SsoMode;
    tenantId?: string;
    inviteToken?: string;
  },
): Promise<void> {
  const { authUrl } = await polaris.auth.initiate({
    connectionId: provider.connectionId,
    query: {
      redirect_url: callbackUrl(),
      ...(opts.mode === "signup" ? { signup: "true" } : {}),
      ...(opts.tenantId ? { tenant_id: opts.tenantId } : {}),
      ...(opts.inviteToken ? { invitation_token: opts.inviteToken } : {}),
    },
  });
  if (!authUrl) throw new Error("SSO initiate did not return an auth URL");
  redirectToIdp(authUrl);
}

/**
 * Start SSO from an address alone, for the "Sign in with SSO" screen where the
 * user names their email and never picks a connection.
 */
export async function startSsoByEmail(email: string): Promise<void> {
  const { authUrl } = await polaris.auth.initiateSsoByEmail({
    body: { email, redirectUrl: callbackUrl() },
  });
  if (!authUrl) throw new Error("SSO initiate did not return an auth URL");
  redirectToIdp(authUrl);
}

/**
 * Hands the browser to the identity provider.
 *
 * `assign`, not `replace`: the IdP is a different origin and the user must be
 * able to come back to the login screen with the browser's Back button when
 * they change their mind there.
 */
export function redirectToIdp(authUrl: string): void {
  window.location.assign(authUrl);
}
